import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { db } from './firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment
} from 'firebase/firestore';

const validNie = /^[0-9]+$/;

export default function App() {
  const [nieInput, setNieInput] = useState('');
  const [sectionSelected, setSectionSelected] = useState('');
  const [sections, setSections] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef();

  // Bajo consumo
  const [lowUsageMode, setLowUsageMode] = useState(false);
  const [loadingLowUsage, setLoadingLowUsage] = useState(true);


  const statusMap = {
    P: 'Presente',
    T: 'Tarde',
    A: 'Ausente',
    J: 'Justificado'
  };

  const statusStyle = (status) => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    color: '#fff',
    fontWeight: 500,
    marginLeft: '0.5rem',
    backgroundColor:
      status === 'P' ? '#28a745' :
        status === 'T' ? '#fd7e14' :
          status === 'A' ? '#dc3545' :
            status === 'J' ? '#17a2b8' :
              '#7f8c8d'
  });

  // Carga de secciones
  useEffect(() => {
    const loadSections = async () => {
      try {
        console.log("Cargando secciones...");
        const q = query(collection(db, 'clases_institucionales'));
        const snap = await getDocs(q);

        if (snap.empty) {
          console.warn("No se encontraron documentos en clases_institucionales");
          setErrorMessage("No se encontraron secciones disponibles.");
          return;
        }

        const list = snap.docs.map(doc => doc.data().section).filter(Boolean);
        const unique = [...new Set(list)];

        // ORDEN HUMANO: Ordena correctamente 1, 2, 10, etc.
        unique.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

        setSections(unique);
        setErrorMessage(""); // Limpiar error si carga bien
        console.log("Secciones cargadas:", unique.length);
      } catch (err) {
        console.error('Error cargando secciones:', err);
        setErrorMessage("Error de conexión: Verifica las reglas de Firebase.");
      }
    };
    loadSections();
  }, []);

  // Validar NIE
  useEffect(() => {
    if (nieInput.trim() && !validNie.test(nieInput.trim())) {
      setErrorMessage('El NIE debe ser un número sin espacios ni letras.');
    } else {
      setErrorMessage('');
    }
  }, [nieInput]);

  // Bajo consumo: consulta al inicio
  useEffect(() => {
    async function checkLowUsage() {
      console.log("Verificando estado del servidor...");
      const today = new Date().toISOString().slice(0, 10);
      const ref = doc(db, 'app_stats', today);
      let lecturasHoy = 0;
      try {
        // Establecer un timeout manual para no bloquear la app
        const statsPromise = getDoc(ref);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout stats")), 3000)
        );

        const snap = await Promise.race([statsPromise, timeoutPromise]);

        if (snap.exists()) {
          lecturasHoy = snap.data().lecturasHoy || 0;
        } else {
          // Intentar crear el doc si no existe, si falla no importa
          setDoc(ref, { lecturasHoy: 0 }).catch(() => { });
        }
      } catch (err) {
        console.warn('No se pudo verificar app_stats, continuando en modo normal:', err.message);
      } finally {
        setLowUsageMode(lecturasHoy >= 40000);
        setLoadingLowUsage(false);
        console.log("App lista. Modo ahorro:", lecturasHoy >= 40000);
      }
    }
    checkLowUsage();
  }, []);

  const isButtonDisabled =
    !(nieInput.trim() && validNie.test(nieInput.trim()) && sectionSelected) ||
    loading || loadingLowUsage || lowUsageMode;

  const handleConsult = async () => {
    if (!captchaValue) {
      setErrorMessage('Por favor, confirma que no eres un robot.');
      setLoading(false);
      return;
    }

    setLoading(true);

    // Verificar cache local (1 consulta por NIE por día)
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = `nie_query_v3_${nieInput.trim()}_${sectionSelected}_${today}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      // Mostrar datos cacheados sin revelar que están en cache
      try {
        setReportData(JSON.parse(cached));
        setLoading(false);
        return; // No consulta Firestore
      } catch (err) {
        // Si hay error al parsear, continúa con consulta normal
        localStorage.removeItem(cacheKey);
      }
    }

    // Incrementa contador de lecturas antes de consultar
    try {
      const today = new Date().toISOString().slice(0, 10);
      const ref = doc(db, 'app_stats', today);
      await updateDoc(ref, { lecturasHoy: increment(1) });
    } catch (err) {
      // Si falla, la consulta igual sigue (no bloquees al usuario)
    }

    try {
      // Buscar la clase por sección
      const classQuery = query(
        collection(db, 'clases_institucionales'),
        where('section', '==', sectionSelected)
      );
      const classSnap = await getDocs(classQuery);

      if (classSnap.empty) {
        setErrorMessage('Sección no encontrada.');
        setLoading(false);
        return;
      }

      const classDoc = classSnap.docs[0];
      const classId = classDoc.id;

      // Buscar el alumno por NIE
      const studentQuery = query(
        collection(db, 'clases_institucionales', classId, 'students'),
        where('nie', '==', nieInput.trim())
      );
      const studentSnap = await getDocs(studentQuery);

      if (studentSnap.empty) {
        setErrorMessage('No se encontró el NIE en esta sección.');
        setLoading(false);
        return;
      }

      const studentDoc = studentSnap.docs[0];
      const studentId = studentDoc.id;
      const studentData = studentDoc.data();

      // OPTIMIZADO: Usar el historial unificado global del alumno
      const historyRef = doc(db, 'students_history', studentId);
      const historySnap = await getDoc(historyRef);

      let attendanceRecords = [];

      if (historySnap.exists()) {
        const historyData = historySnap.data();
        const historyMap = historyData.history || {};
        
        // Convertir mapa de historial en lista y filtrar por el ID de la clase actual
        attendanceRecords = Object.values(historyMap)
          .filter(entry => entry.orig === classId)
          .map(entry => ({
            date: new Date(entry.d + 'T12:00:00')
              .toLocaleDateString('es-SV', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }),
            status: entry.s,
            rawDate: entry.d,
            reg: entry.r,
            time: entry.t || '--:--'
          }))
          .sort((a, b) => {
            if (b.rawDate !== a.rawDate) return b.rawDate.localeCompare(a.rawDate);
            return b.reg - a.reg;
          });
      }

      const fullName = `${studentData.ap || studentData.apellidos || ''}, ${studentData.nom || studentData.nombres || ''}`;

      const reportDataToSave = {
        fullName,
        section: sectionSelected,
        attendance: attendanceRecords
      };

      setReportData(reportDataToSave);

      // Guardar en localStorage para evitar consultas repetidas el mismo día
      try {
        localStorage.setItem(cacheKey, JSON.stringify(reportDataToSave));
      } catch (err) {
        // Si falla el guardado (storage lleno), no es crítico
        console.warn('No se pudo guardar en cache local', err);
      }
    } catch (err) {
      console.error('Error consultando Firestore', err);
      setErrorMessage('Ocurrió un error al consultar los datos.');
    } finally {
      setLoading(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaValue(null);
    }
  };

  // Render
  return (
    <div style={{ padding: '2rem', maxWidth: '400px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <img src="/banner_v2.png" alt="Logo" style={{ width: '141px', height: '39px' }} />
      </div>

      {/* Título */}
      <h1 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Consulta de asistencia para padres</h1>

      {/* Si modo bajo consumo está activo */}
      {loadingLowUsage ? (
        <p style={{ textAlign: 'center' }}>Cargando...</p>
      ) : lowUsageMode ? (
        <div style={{ color: '#c0392b', background: '#fffbe7', border: '1px solid #ffe08a', borderRadius: 8, padding: '1rem', margin: '1rem 0', textAlign: 'center' }}>
          <h3 style={{ marginTop: 0 }}>Modo ahorro activado</h3>
          <p>Por alta demanda hoy, la consulta estará disponible nuevamente mañana.<br />
            Si tienes dudas urgentes, contacta directamente al Instituto.</p>
        </div>
      ) : (
        <>
          {/* Campo NIE */}
          <div style={{ marginBottom: '1rem' }}>
            <label>NIE:</label>
            <input
              type="text"
              value={nieInput}
              onChange={(e) => setNieInput(e.target.value)}
              placeholder="Ingrese el NIE"
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', fontSize: '1rem' }}
            />
          </div>

          {/* Dropdown sección */}
          <div style={{ marginBottom: '1rem' }}>
            <label>Sección:</label>
            <select
              value={sectionSelected}
              onChange={(e) => setSectionSelected(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem', fontSize: '1rem' }}
            >
              <option value="">Seleccione una sección</option>
              {sections.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
          </div>

          {/* Mensaje de error */}
          {errorMessage && <p style={{ color: '#dc3545', fontWeight: 'bold', textAlign: 'center' }}>{errorMessage}</p>}

          {/* Captcha */}
          <div style={{
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'center',
            transform: 'scale(0.85)',
            transformOrigin: 'center center'
          }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(val) => setCaptchaValue(val)}
            />
          </div>

          {/* Botón */}
          <button 
            onClick={handleConsult} 
            disabled={isButtonDisabled || !captchaValue} 
            style={{ 
              width: '100%', 
              padding: '0.75rem', 
              fontSize: '1rem',
              background: (!captchaValue || isButtonDisabled) ? '#ccc' : '#008040',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: (!captchaValue || isButtonDisabled) ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </button>
        </>
      )}

      {/* Reporte */}
      {reportData && (
        <div className="report-container" style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '1.5rem', marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Reporte de Asistencia</h3>
          <p><strong>NIE:</strong> {nieInput}</p>
          <p><strong>Nombre Completo:</strong> {reportData.fullName}</p>
          <p><strong>Sección:</strong> {reportData.section}</p>
          <h4>Historial de Asistencia:</h4>
          {reportData.attendance.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem', textAlign: 'center' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: '8px' }}>Fecha</th>
                    <th style={{ padding: '8px' }}>Hora</th>
                    <th style={{ padding: '8px' }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.attendance.slice(0, visibleCount).map((record, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f9f9f9' }}>
                      <td style={{ padding: '8px', fontSize: '0.9rem' }}>{record.date}</td>
                      <td style={{ padding: '8px', fontSize: '0.9rem' }}>{record.time}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={statusStyle(record.status)}>
                          {statusMap[record.status] || record.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {reportData.attendance.length > visibleCount && (
                <button
                  onClick={() => setVisibleCount(prev => prev + 5)}
                  style={{
                    background: 'white',
                    color: '#008040',
                    border: '1px solid #008040',
                    padding: '8px',
                    fontSize: '0.9rem',
                    marginBottom: '1rem',
                    boxShadow: 'none'
                  }}
                >
                  Cargar más...
                </button>
              )}
            </>
          ) : (
            <p>No hay registros de asistencia.</p>
          )}
          <p style={{ textAlign: 'center', marginBottom: '0.5rem', fontWeight: 600 }}>Consulta de asistencia para padres</p>
          <p style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>
            Recuerde presentar las justificaciones correspondientes por las ausencias en los períodos detallados en el Manual de Convivencia.
          </p>
        </div>
      )}

      {/* Footer Permanente */}
      <footer style={{ marginTop: '3rem', paddingBottom: '1rem', borderTop: '1px solid #eee', paddingTop: '1.2rem' }}>
        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#888', margin: 0 }}>
          ProfeSV, tecnología educativa al alcance de todos.
        </p>
        <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#bbb', marginTop: '4px' }}>
          v2.1.0
        </p>
      </footer>
    </div>
  );
}
